import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Contexts from "../../contexts/Contexts";
import { userService } from "../ApiUser";
import { saveUserCache, loadUserCache, clearUserCache } from "../../utils/userCacheUtils";
import { getCookie } from "../../utils/cookieUtils";
import { AppNotification } from "../../utils/notificationTypes";

interface FriendProfile {
    avatar: string;
    phone_number?: string;
    cpf?: string;
    email?: string;
}

interface Friend {
    id: number;
    first_name: string;
    last_name: string;
    profile: FriendProfile;
}

interface FriendResponse {
    friend?: Friend;
    friend_phone_number: string;
}

export interface FriendData {
    id: number | string; // Can be either friend.id or friend_phone_number
    name: string; // Formatted name or phone number if no friend object
    avatar: string;
    phoneNumber: string;
}

export const useSendVerifyCode = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>(null);
    const [success, setSuccess] = useState(false);
    const { t } = useTranslation();
    const { setErrorMessage } = useContext(Contexts);
    const [successSavePassword, setSuccessSavePassword] = useState(false);
    const sendVerifyCode = async (phoneNumber: string, op?: number) => {
        setError(null);
        setSuccess(false);
        setLoading(true);
        const requestData: any = { "phone_number": `+${phoneNumber}` };
        if (op !== undefined) {
            requestData.op = op;
        }
        const response = await userService.sendVerifyCode(requestData);
        if (response.code === 0) {
            setSuccess(true);
            setLoading(false);
            return response.data;
        } else {
            if (response.code === 1003) {
                response.message = t('errors.verify.tooFrequent')
                setErrorMessage(response);
            }
            setLoading(false);
            setError(response);
        }
    };
    const [verifyCodeToken, setVerifyCodeToken] = useState<string | null>(null);
    const verifyCode = async (phoneNumber: string, code: string, op?: number) => {
        setError(null);
        setSuccess(false);
        setLoading(true);
        const requestData: any = { "phone_number": `+${phoneNumber}`, "verification_code": code };
        if (op !== undefined) {
            requestData.op = op;
        }
        const response = await userService.verifyCode(requestData);
        if (response.code === 0) {
            setSuccess(true);
            setLoading(false);
            setVerifyCodeToken(response.data.token);
            return response.data;
        } else {
            if (response.code === 1004) {
                response.message = t('errors.verify.invalidCode')
                setErrorMessage(response);
            }
            setLoading(false);
            setError(response);
        }
    };
    const savePassword = async (password: string, token: string) => {
        setError(null);
        setSuccess(false);
        setLoading(true);
        const response = await userService.savePassword({ password, token });
        if (response.code === 0) {
            setSuccessSavePassword(true);
            setLoading(false);
            return response.data;
        } else {
            if (response.code === 1005) {
                response.message = t('errors.password.saveFailed')
                setErrorMessage(response);
            }
            setLoading(false);
            setError(response.code);
        }
    }

    return {
        success, sendVerifyCode, loading, error, verifyCode, verifyCodeToken, savePassword, successSavePassword
    };
}

// Etapa-1 phone-availability probe. Resolves the WhatsApp/Telefone field to an
// existing account BEFORE the partner application is submitted, so the form can
// branch: known number → log in, new number → SMS-verified create. Returns
// true (registered), false (free), or null when the probe itself failed
// (network/throttle) so the caller can surface a retry instead of guessing.
// NOTE: the client interceptor already unwraps the { data, code } envelope, so
// we read response.data.registered — one .data — like every other hook here.
export const useCheckPhone = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>(null);

    const checkPhone = async (phoneNumber: string): Promise<boolean | null> => {
        setError(null);
        setLoading(true);
        const response = await userService.checkPhone(phoneNumber);
        setLoading(false);
        if (response.code === 0 && response.data && typeof response.data.registered === "boolean") {
            return response.data.registered;
        }
        setError(response);
        return null;
    };

    return { checkPhone, loading, error };
};

export const useLogin = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>(null);
    const [success, setSuccess] = useState(false);
    const { setErrorMessage } = useContext(Contexts);
    const { t } = useTranslation();
    const { fetchUserData } = useGetUserData();

    const login = async ({ phoneNumber, password }: { phoneNumber: string; password: string }) => {
        setError(null);
        setSuccess(false);
        setLoading(true);
        const response = await userService.login({ phoneNumber, password });
        setLoading(false);

        if (response.code === 0) {
            setSuccess(true);
            // Set user info from login response
            fetchUserData();
            // if (response.data) {
            //     setUser({
            //         id: response.data.id || 0,
            //         name: response.data.first_name || response.data.name || "User",
            //         surname: response.data.last_name || response.data.surname,
            //         phoneNumber: response.data.profile?.phone_number || phoneNumber,
            //         avatar: response.data.avatar || "",
            //     });
            //     setUserId(response.data.id || 0);
            // }
            return response.data;
        } else {
            if (response.code === 1002) {
                setError({ message: t('errors.auth.invalidCredentials'), name: "password" });
            } else if (response.message === 'Enter a valid phone number.') {
                setError({ message: t('verify.phoneValidation'), name: "phoneNumber" });
            } else {
                setErrorMessage(response);

            }
        }
    };
    return { login, loading, error, success };
}
interface RegisterData {
    phoneNumber: string;
    verifyCode: string;
    password: string;
    birthDate: string;
    params: string;
}
export const useRegister = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>(null);
    const [success, setSuccess] = useState(false);
    const { t } = useTranslation();
    const { setErrorMessage } = useContext(Contexts);
    const { fetchUserData } = useGetUserData();
    const register = async (data: RegisterData) => {
        setError(null);
        setSuccess(false);
        setLoading(true);
        const d = data.birthDate.split("/");
        const refToken = getCookie('lvs_ref') || localStorage.getItem('lvs_ref') || '';
        // Bundle 4c: MLM referral code from /r/{code} cookie (separate from affiliate ref_token)
        const mlmRefCode = getCookie('mlm_ref') || localStorage.getItem('mlm_ref') || '';
        const poseData = {
            "phone_number": "+" + data.phoneNumber,
            "verification_code": data.verifyCode,
            "password": data.password,
            "birthday": d[2] + "-" + d[1] + "-" + d[0],
            "params": data.params,
            "ref_token": refToken,
            "mlm_ref_code": mlmRefCode,
        }
        const response = await userService.register(poseData);
        if (response.code === 0) {
            const loginResponse = await userService.login({
                phoneNumber: poseData.phone_number,
                password: poseData.password
            });
            if (loginResponse.code === 0) {
                fetchUserData();
                setLoading(false);
                setSuccess(true);
                // Clear MLM referral cookie after successful attribution
                if (mlmRefCode) {
                    document.cookie = 'mlm_ref=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/';
                    localStorage.removeItem('mlm_ref');
                }
                return response;
            } else {
                // Auto-login after registration failed — account was created, ask user to log in manually
                setError({
                    message: t("errors.auth.autoLoginFailed") || "Conta criada com sucesso. Por favor, faça login.",
                    name: "autoLogin",
                });
                setLoading(false);
                setSuccess(true); // Registration itself succeeded
                return response;
            }
        } else if (response.code === 1004) {
            response.message = t("errors.verify.invalidCode")
            setErrorMessage(response);

        } else {
            setError(response);
            // setErrorMessage(response);
        }
        setLoading(false);


    };

    return {
        success, register, loading, error
    };
}
export const useUserInfo = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>(null);
    const [success, setSuccess] = useState(false);
    const setUserInfo = async (data: {
        name?: string;
        surname?: string;
        cpf?: string;
        email?: string;
        nickName?: string; // profile.profile.nick_name
        username?: string; // profileName -> username
    }) => {
        setError(null);
        setSuccess(false);
        setLoading(true);
        // 只发送有传入的字段，未传入的不发送
        const payload: any = {};
        if (data.name !== undefined && data.name !== null && data.name !== "") {
            payload.first_name = data.name;
        }
        if (data.surname !== undefined && data.surname !== null && data.surname !== "") {
            payload.last_name = data.surname;
        }
        if (data.cpf !== undefined && data.cpf !== null && data.cpf !== "") {
            const sanitizedCpf = data.cpf.replace(/\D/g, "");
            payload.CPF = sanitizedCpf;
        }
        if (data.email !== undefined && data.email !== null && data.email !== "") {
            payload.email = data.email;
        }
        if (data.nickName !== undefined && data.nickName !== null && data.nickName !== "") {
            payload.nick_name = data.nickName;
        }
        if (data.username !== undefined && data.username !== null && data.username !== "") {
            payload.username = data.username;
        }
        const response = await userService.setUserInfo(payload);
        if (response.code === 0) {
            setSuccess(true);
        } else {
            setError(response.code);
        }
        setLoading(false);
        return response;

    };

    return {
        success, setUserInfo, loading, error
    };
}

// Hook to manage user following status
export const useUserFollowingStatus = (targetUserId: number | null) => {
    const [isFollowing, setIsFollowing] = useState<boolean | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>(null);
    const { userId } = useContext(Contexts);

    const checkFollowingStatus = useCallback(async () => {
        if (!targetUserId || !userId) {
            setIsFollowing(null);
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            // 获取当前用户的关注列表
            const response = await userService.getFollowingList(userId);
            if (response && response.code === 0) {
                const followingList = response.data?.results || response.data || [];
                const isCurrentlyFollowing = followingList.some((followedUser: any) => 
                    followedUser.id === targetUserId || followedUser.user?.id === targetUserId
                );
                setIsFollowing(isCurrentlyFollowing);
            } else {
                setError(response?.code || 'Failed to fetch following status');
                setIsFollowing(false);
            }
        } catch (err) {
            setError(err);
            setIsFollowing(false);
        } finally {
            setLoading(false);
        }
    }, [targetUserId, userId]);

    useEffect(() => {
        checkFollowingStatus();
    }, [checkFollowingStatus]);

    return {
        isFollowing,
        loading,
        error,
        refetch: checkFollowingStatus
    };
};

export const useGetUserData = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>(null);
    const [success, setSuccess] = useState(false);
    const { setCartCount, setUserId, setUser, setNotificationsCount, setUserInfoResolved } = useContext(Contexts);
    const [userData, setUserData] = useState<any>(null);

    /**
     * 从本地缓存加载用户数据（同步操作）
     * 用于页面初始化时快速显示用户信息
     */
    // Bug 9 fix: validate cached session against backend before trusting it.
    // Prevents stale localStorage from keeping user "logged in" after logout in another window.
    // Non-blocking: cache is shown optimistically, then cleared if backend rejects.
    const loadCachedUserData = useCallback(async () => {
        const cachedData = loadUserCache();
        if (!cachedData) return false;

        // Optimistically apply cache so UI renders immediately
        setUser(cachedData.user);
        setUserId(cachedData.userId);
        setCartCount(cachedData.cartCount);
        setNotificationsCount(cachedData.notificationsCount);

        // Then validate against backend in background (fire-and-forget, must not block FCP)
        try {
            const validation = await userService.getUserInfoId();
            if (validation.code !== 0 || !validation.data?.id) {
                // Session invalid server-side — clear everything
                clearUserCache();
                setUser(null);
                setUserId(0);
                setCartCount(0);
                setNotificationsCount(0);
                return false;
            }
            // This validation call IS GET /api/user/info/, so it already carries the server's
            // gifting_enabled — seed it now rather than waiting for fetchUserData, which
            // initializeApp defers behind requestIdleCallback(timeout: 2000). Without this the
            // gifting gate stays 'undetermined' for up to ~2s + a second round-trip, which a
            // route guard has to sit out on a spinner. Overwrite the cached user's flag: the
            // cache is 7-day and can carry a value baked by an older build.
            if (cachedData.user) {
                setUser({
                    ...cachedData.user,
                    giftingEnabled: validation.data.gifting_enabled === true,
                });
            }
        } catch (err) {
            // Network error — leave cache in place (offline tolerance)
            console.warn('[auth] Session validation failed (network?), keeping cache:', err);
        } finally {
            // Settled, whatever the outcome. On a network error we fall back to the cached
            // flag rather than spinning forever — the build-time flag still bounds it, so a
            // flag-off build stays dark no matter what the cache says.
            setUserInfoResolved(true);
        }

        return true;
    }, [setCartCount, setUserId, setUser, setNotificationsCount, setUserInfoResolved]);

    const fetchUserDataInner = useCallback(async (fast_cart: number = 0) => {
        // if (fast_cart) {
        //     setCartCount(cartCount + fast_cart);
        // }
        setError(null);
        setSuccess(false);
        setLoading(true);
        // 第一步：通过 /api/user/info/ 获取当前用户ID
        const idResp = await userService.getUserInfoId();
        if (idResp.code !== 0 || !idResp.data || !idResp.data.id) {
            // 如果请求失败（如401或403认证错误），创建临时用户，让流程能够继续进行
            if (idResp.code === 401 || idResp.code === 403) {
                clearUserCache();
                const guestUser = {
                    id: 0,
                    name: "Guest",
                    isGuest: true,
                };
                setUser(guestUser);
                setUserId(0);
                setCartCount(0);
                setNotificationsCount(0);
                setSuccess(false);
                setError(idResp.code);
                setLoading(false);
                // 临时用户不保存到缓存
                return;
            }
            // 其他错误情况，设置错误状态
            setError(idResp.code);
            setLoading(false);
            return;
        }
        const userIdVal = idResp.data.id;

        // 从 info 接口中也取出计数字段（如果存在）
        const infoCartCount = idResp.data.cart_items_count;
        const infoUnreadCount = idResp.data.notifications_unread_count;

        // 检查是否为guest用户（profile.is_guest为true）
        const isGuest = idResp.data.profile?.is_guest === true;

        // 如果是guest用户，不调用/user/{id}/接口，直接使用info接口返回的数据
        if (isGuest) {
            const infoData = idResp.data;
            setSuccess(true);
            setUserId(userIdVal);
            
            const cartCount = typeof infoCartCount === "number" ? infoCartCount : 0;
            const notificationsCount = typeof infoUnreadCount === "number" ? infoUnreadCount : 0;
            
            setCartCount(cartCount);
            setNotificationsCount(notificationsCount);

            const userInfo = {
                id: userIdVal,
                name: infoData.first_name || infoData.username || "Guest",
                surname: infoData.last_name,
                phoneNumber: infoData.profile?.phone_number,
                avatar: infoData.profile?.avatar || "",
                cpf: infoData.profile?.CPF || "",
                hasCpf: infoData.profile?.has_cpf === true,
                email: infoData.email || infoData.profile?.email || "",
                username: infoData.username || "",
                nickName: infoData.profile?.nick_name,
                profileName: infoData.profile_name || infoData.username || "",
                birthday: infoData.profile?.birthday,
                followerCount: infoData.profile?.follower_count,
                followingCount: infoData.profile?.following_count,
                postCount: infoData.profile?.post_count,
                hideBadges: infoData.profile?.hide_badges,
                hideStatistics: infoData.profile?.hide_statistics,
                isGuest: true,
            };

            setUser(userInfo);
            setUserData(infoData);

            // 保存到缓存
            saveUserCache({
                user: userInfo,
                userId: userIdVal,
                cartCount,
                notificationsCount,
            });
            setLoading(false);
            return;
        }

        // 第二步：通过 /api/user/{id}/ 获取完整信息（非guest用户）
        const detailResp = await userService.getUserById(userIdVal);
        if (detailResp.code === 0) {
            setSuccess(true);
            const detail = detailResp.data;
            setUserId(userIdVal);
            
            const cartCount = typeof infoCartCount === "number" ? infoCartCount : 0;
            const notificationsCount = typeof infoUnreadCount === "number" ? infoUnreadCount : 0;
            
            setCartCount(cartCount);
            setNotificationsCount(notificationsCount);

            const userInfo = {
                id: userIdVal,
                name: detail.first_name || "User",
                surname: detail.last_name,
                phoneNumber: detail.profile?.phone_number,
                avatar: detail.profile?.avatar || "",
                cpf: detail.profile?.CPF || "",
                hasCpf: detail.profile?.has_cpf === true,
                email: detail.email || detail.profile?.email || "",
                username: detail.username,
                nickName: detail.profile?.nick_name,
                profileName: detail.profile_name || detail.username || "",
                birthday: detail.profile?.birthday,
                followerCount: detail.profile?.follower_count,
                followingCount: detail.profile?.following_count,
                postCount: detail.profile?.post_count,
                hideBadges: detail.profile?.hide_badges,
                hideStatistics: detail.profile?.hide_statistics,
                isGuest: false, // 真实用户，非临时用户
                is_affiliate_approved: detail.is_affiliate_approved === true,
                is_affiliate_master: detail.is_affiliate_master === true,
                referral_code: detail.profile?.referral_code || '',
                has_visited_referrals: detail.profile?.has_visited_referrals === true,
                // Server-driven gifting master switch (G4a). NOTE: it lives on
                // UserInfoSerializer — i.e. `idResp` (GET /api/user/info/), NOT on `detail`
                // (GET /api/user/{id}/, a different serializer that does not carry it).
                // Reading it off `detail` would be `undefined` forever and silently gate
                // gifting off with no error. The build-time flag is ANDed in at the point of
                // use (useGiftingGate), so runtime can only ever NARROW what the build allows.
                giftingEnabled: idResp.data.gifting_enabled === true,
            };

            setUser(userInfo);
            setUserData(detail);

            // 保存到缓存
            saveUserCache({
                user: userInfo,
                userId: userIdVal,
                cartCount,
                notificationsCount,
            });
            setLoading(false);
            // SB4: return the resolved user so post-login routing can switch on
            // fresh flags (is_affiliate_master) without racing the setUser update.
            return userInfo;
        } else {
            setError(detailResp.code);
        }
        setLoading(false);
    }, [setCartCount, setUserId, setUser, setNotificationsCount]);

    /**
     * Thin wrapper whose ONLY job is to flip `userInfoResolved` on EVERY outcome — authed
     * success, 403-anonymous, backend-guest, API error, and a THROWN network failure.
     *
     * The gifting route guards block on this signal, so a path that fails to flip it strands
     * them on a spinner forever. A `finally` here proves total coverage; setting the flag at
     * each `return` inside the body would not survive the next early-return someone adds.
     * Deliberately NOT the existing `success` flag: that stays false for a logged-out visitor
     * (the 403 branch calls setSuccess(false)), which would hang every anonymous session.
     */
    const fetchUserData = useCallback(async (fast_cart: number = 0) => {
        try {
            return await fetchUserDataInner(fast_cart);
        } finally {
            setUserInfoResolved(true);
        }
    }, [fetchUserDataInner, setUserInfoResolved]);

    return { fetchUserData, loadCachedUserData, loading, error, success, userData };
}

export const useUserBalance = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>(null);
    const [balance, setBalance] = useState<{ type: number; amount: string } | null>(null);

    const fetchBalance = useCallback(async () => {
        setError(null);
        setLoading(true);
        const response = await userService.getUserBalance();
        if (response.code === 0) {
            // 后端返回的是数组，取第一个元素
            const balanceData = Array.isArray(response.data) && response.data.length > 0 
                ? response.data[0] 
                : response.data;
            setBalance(balanceData);
        } else {
            setError(response.code);
        }
        setLoading(false);
        return response;
    }, []);

    return {
        balance,
        fetchBalance,
        loading,
        error,
    };
}

export const useLogout = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>(null);
    const [success, setSuccess] = useState(false);
    const { setUserId, setUser, setCartCount, setNotificationsCount } = useContext(Contexts);
    const navigate = useNavigate();
    const logout = async () => {
        setError(null);
        setSuccess(false);
        setLoading(true);
        try {
            await userService.logout();
        } catch (err) {
            // session may be dead server-side; clear locally regardless
        } finally {
            // Clear all user information unconditionally
            setUserId(null);
            setUser(null);
            setCartCount(0);
            setNotificationsCount(0);
            clearUserCache();
            setSuccess(true);
            setLoading(false);
            navigate('/');
        }
    };
    return { logout, loading, error, success };
}
export const useNotifications = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>(null);
    const [success, setSuccess] = useState(false);
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const { notificationsCount, setNotificationsCount } = useContext(Contexts);
    const fetchNotifications = async (isRead: boolean | null) => {
        setLoading(true);
        const response = await userService.getNotifications(isRead);
        if (response.code === 0) {
            setSuccess(true);
            setNotifications(response.data.map((item: any) => ({
                id: item.id,
                title: item.title,
                content: item.content,
                isRead: item.is_read,
                createdAt: new Date(item.create_time).getTime(),
                // `type` is the NotificationTemplate.Type integer (BE-1). An API build
                // without BE-1 omits it — null, so the row renders generically.
                type: typeof item.type === "number" ? item.type : null,
                // Serialized by the API all along; the FE simply dropped it until now.
                ticket: typeof item.ticket === "number" ? item.ticket : null,
            })));
        } else {
            setError(response.code);
        }
        setLoading(false);
    };
    const markAllAsRead = async () => {
        const response = await userService.readAllNotifications();
        if (response.code === 0) {
            setSuccess(true);
            setNotifications(notifications.map(item => ({ ...item, isRead: true })));
            setNotificationsCount(0);
        } else {
            setError(response.code);
        }
    };
    /** Mark ONE row read (POST /api/notification/read/ takes an id_list). */
    const markAsRead = async (id: number) => {
        const target = notifications.find(item => item.id === id);
        // Already read, or no longer in the list: no call, and — the point of the
        // guard — no second decrement of the unread badge.
        if (!target || target.isRead) return;
        const response = await userService.readNotifications([id]);
        if (response.code === 0) {
            setSuccess(true);
            setNotifications(prev =>
                prev.map(item => (item.id === id ? { ...item, isRead: true } : item)),
            );
            setNotificationsCount(Math.max(0, notificationsCount - 1));
        } else {
            setError(response.code);
        }
    };
    return { fetchNotifications, loading, error, success, notifications, markAllAsRead, markAsRead };
}
export const useStreak = () => {
    const [streak, setStreak] = useState<string[]>([]);
    const [error, setError] = useState<any>(null);
    const fetchStreak = async () => {
        const response = await userService.getStreak();
        if (response.code === 0) {
            setStreak(response.data);
        } else {
            setError(response.code);
        }
    };
    return { streak, fetchStreak, error };
}

export const useGetBadges = () => {
    const processBadgeData = (badgeData: any) => {
        const { badges, user_badges } = badgeData;

        // 创建用户已获得徽章的映射
        const userBadgeMap = new Map<number, string>();
        user_badges.forEach((userBadge: any) => {
            userBadgeMap.set(userBadge.badge, userBadge.create_time);
        });

        // 处理所有徽章，添加状态信息
        const badgeList: any[] = badges.map((badge: any) => {
            const isReceived = userBadgeMap.has(badge.id);
            const receivedTime = userBadgeMap.get(badge.id);

            return {
                ...badge,
                label: badge.name,
                icon: badge.icon_received,
                isReceived: isReceived,
                receivedTime: receivedTime || undefined
            };
        });
        //排序,用户获得的徽章放在前面,获得越晚的越靠前
        return badgeList.sort((a, b) => {
            if (a.isReceived && !b.isReceived) {
                return -1;
            } else if (!a.isReceived && b.isReceived) {
                return 1;
            }
            if (a.receivedTime && b.receivedTime) {
                return new Date(b.receivedTime).getTime() - new Date(a.receivedTime).getTime();
            }
            return 0;
        });
    }
    const [badges, setBadges] = useState<any[]>([]);
    const [error, setError] = useState<any>(null);
    const fetchBadges = async (userId: number | string) => {
        const response = await userService.getBadges(userId);
        if (response.code === 0) {
            setBadges(processBadgeData(response.data));
        } else {
            setError(response.code);
        }
    }
    return { badges, fetchBadges, error };
}
export const buildNameString = (nameObj: any) => {
    const name = nameObj.first_name || nameObj.name || "";
    const surname = nameObj.last_name || nameObj.surname || "";
    if (name === "" && surname === "") {
        return "Anonymous";
    }
    return name + " " + surname;
}
export const useGetFriends = () => {
    const [friends, setFriends] = useState<FriendData[]>([]);
    const [error, setError] = useState<any>(null);

    const fetchFriends = async () => {
        const response = await userService.getFriends();
        if (response.code === 0) {
            setFriends(response.data.map((item: FriendResponse) => ({
                id: item.friend ? item.friend.id : item.friend_phone_number,
                name: item.friend ? buildNameString(item.friend) : item.friend_phone_number,
                avatar: item.friend?.profile.avatar || "",
                phoneNumber: item.friend_phone_number || "",
            })));
        } else {
            setError(response.code);
        }
    }
    return { friends, fetchFriends, error };
}